import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useI18n } from '@/i18n'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { siteConfig } from '@/config/site.config'
import { isValidEmail } from '@/lib/format'
import { useSession } from '@/hooks/useSession'
import { useOwner } from '@/hooks/useOwner'

/**
 * Sign in / create an account for boutique studio.
 */
export default function SignIn() {
  const { t } = useI18n()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { signIn, signUp } = useSession()
  const { ownerEmail } = useOwner()

  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const redirect = params.get('redirect') || '/studio'
  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const next: Record<string, string> = {}
    if (!isValidEmail(form.email)) next.email = t('auth.errorEmail')
    if (form.password.length < 6) next.password = t('auth.errorPassword')
    setErrors(next)
    if (Object.keys(next).length) return

    setBusy(true)
    const result =
      mode === 'signin'
        ? await signIn(form.email, form.password)
        : await signUp(form.email, form.password, form.name)
    setBusy(false)

    if (result.error) {
      setErrors({ password: result.error })
      return
    }
    toast(t('auth.signedIn'))
    navigate(redirect)
  }

  return (
    <div data-upbo-id="page-signin" data-upbo-label="Sign in page">
      <Container size="sm">
        <div className="py-16 sm:py-24 max-w-md mx-auto flex flex-col gap-8">
          {/* ── Wordmark ─────────────────────────────────────────────────── */}
          <Link to="/" className="flex flex-col items-center leading-none">
            <span className="font-display text-[28px] tracking-[0.22em] pl-[0.22em]">{siteConfig.name}</span>
          </Link>

          <div className="flex flex-col gap-2 text-center">
            <h1 className="font-display text-[34px] leading-tight">
              {mode === 'signin' ? t('auth.signInTitle') : t('auth.signUpTitle')}
            </h1>
            <p className="text-sm text-text-muted">
              {mode === 'signin' ? t('auth.signInSubtitle') : t('auth.signUpSubtitle')}
            </p>
          </div>

          {/* ── Demo credentials for boutique studio ───── */}
          <div className="border border-border rounded-squircle p-4 flex flex-col gap-1.5 bg-surface-2">
            <span className="eyebrow">{t('auth.ownerHintTitle')}</span>
            <p className="text-[13px] text-text-muted leading-relaxed">
              {t('auth.ownerHintBody', { email: ownerEmail })}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <button
                type="button"
                onClick={() => {
                  set('email', ownerEmail)
                  set('password', 'tempora123')
                }}
                className="text-[12px] text-primary hover:underline font-medium"
              >
                {ownerEmail} (tempora123)
              </button>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-upbo-id="auth-form"
            data-upbo-type="form"
            data-upbo-label="Auth form"
            className="flex flex-col gap-4"
          >
            {mode === 'signup' && (
              <Input
                name="name"
                label={t('auth.name')}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            )}
            <Input
              name="email"
              type="email"
              autoComplete="email"
              label={t('auth.email')}
              value={form.email}
              error={errors.email}
              onChange={(e) => set('email', e.target.value)}
            />
            <Input
              name="password"
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              label={t('auth.password')}
              hint={t('auth.passwordHint')}
              value={form.password}
              error={errors.password}
              onChange={(e) => set('password', e.target.value)}
            />
            <Button type="submit" size="lg" fullWidth loading={busy} className="mt-2">
              {mode === 'signin' ? t('auth.signIn') : t('auth.signUp')}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => {
              setMode((m) => (m === 'signin' ? 'signup' : 'signin'))
              setErrors({})
            }}
            className="text-[12px] text-text-muted hover:text-text transition-colors"
          >
            {mode === 'signin' ? t('auth.toSignUp') : t('auth.toSignIn')}
          </button>
        </div>
      </Container>
    </div>
  )
}
